// src/components/CoachPostImage.js — the image on a "from Danielle" post.
//
// ONE definition for the member Dashboard and the admin editor's preview, so
// the preview can never drift from what members actually see.
//
// The image is ALWAYS shown whole (never cropped): it keeps its own shape and
// sits inside a size box chosen by Danielle. Tapping opens it full screen so
// small details in a screenshot stay readable. (Before 2026-10-06 the image
// was stretched to the full card width and cut off at 300px, so a phone
// screenshot became a giant zoomed slice of its top.)

import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export const POST_IMAGE_SIZES = ['small', 'medium', 'large'];

// Absent / unknown ⇒ medium (every post saved before sizes existed).
export function normalizePostImageSize(s) {
    return POST_IMAGE_SIZES.includes(s) ? s : 'medium';
}

const BOX = {
    small: { maxWidth: 220, maxHeight: 260 },
    medium: { maxWidth: 380, maxHeight: 420 },
    large: { maxWidth: '100%', maxHeight: 'min(560px, 70vh)' },
};

function Lightbox({ src, onClose, zIndex = 3200 }) {
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
    }, [onClose]);

    return createPortal(
        <div
            role="dialog" aria-modal="true" aria-label="Image"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            style={{
                position: 'fixed', inset: 0, zIndex,
                background: 'rgba(36, 28, 28, 0.86)',
                backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 'max(20px, env(safe-area-inset-top)) 20px max(20px, env(safe-area-inset-bottom))',
                animation: 'fadeIn 0.18s ease', cursor: 'zoom-out',
            }}
        >
            <img src={src} alt="" referrerPolicy="no-referrer"
                style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.4)', display: 'block' }} />
            <button type="button" aria-label="Close image"
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                style={{
                    position: 'absolute', top: 'max(14px, env(safe-area-inset-top))', right: 14,
                    width: 40, height: 40, borderRadius: '50%', border: 'none', cursor: 'pointer',
                    background: 'rgba(255,255,255,0.16)', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
        </div>,
        document.body,
    );
}

// `lightboxZIndex` lets a host that itself sits very high (the announcement
// pop-up is zIndex 10000) open the full-screen view ABOVE itself. Absent ⇒
// 3200, the original value, so Coach Posts are unchanged.
function CoachPostImage({ src, size, style, lightboxZIndex }) {
    const [open, setOpen] = useState(false);
    const [broken, setBroken] = useState(false);
    const close = useCallback(() => setOpen(false), []);
    useEffect(() => { setBroken(false); }, [src]);
    if (!src || broken) return null;
    const box = BOX[normalizePostImageSize(size)];

    return (
        <>
            <button
                type="button"
                aria-label="Open image full screen"
                onClick={() => setOpen(true)}
                style={{
                    display: 'inline-block', position: 'relative', padding: 0, margin: 0,
                    maxWidth: box.maxWidth === '100%' ? '100%' : `min(${box.maxWidth}px, 100%)`,
                    borderRadius: 14, overflow: 'hidden', cursor: 'zoom-in',
                    background: '#fff',
                    border: '1px solid color-mix(in srgb, var(--mom-charcoal) 10%, transparent)',
                    boxShadow: '0 1px 0 rgba(255,255,255,0.6) inset, 0 6px 22px rgba(62,47,47,0.10)',
                    verticalAlign: 'top',
                    ...style,
                }}
            >
                <img
                    src={src} alt="" referrerPolicy="no-referrer" loading="lazy"
                    onError={() => setBroken(true)}
                    style={{
                        display: 'block', width: 'auto', height: 'auto',
                        maxWidth: '100%',
                        maxHeight: box.maxHeight,
                    }}
                />
                {/* Small "enlarge" cue so members know it opens */}
                <span aria-hidden="true" style={{
                    position: 'absolute', right: 8, bottom: 8, width: 28, height: 28, borderRadius: '50%',
                    background: 'rgba(255,255,255,0.88)', boxShadow: '0 2px 8px rgba(0,0,0,0.14)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--mom-charcoal)',
                }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" /></svg>
                </span>
            </button>
            {open && <Lightbox src={src} onClose={close} zIndex={lightboxZIndex} />}
        </>
    );
}

export default CoachPostImage;
